import { BusinessSubscriptionType, SubscriptionType } from '../types/misc';
import { Feature, CommunityFeatures } from './FeatureUtils';

/**
 * Define which subscription tiers are available and their "coming soon" status
 */
export const businessSubscriptionConfig = {
  [BusinessSubscriptionType.STARTER]: {
    isComingSoon: false,
  },
  [BusinessSubscriptionType.PROFESSIONAL]: {
    isComingSoon: false,
  },
};

/**
 * Base monthly prices for each business subscription tier (in euros)
 */
export const businessBaseSubscriptionPrices = {
  [BusinessSubscriptionType.STARTER]: 15.0,
  [BusinessSubscriptionType.PROFESSIONAL]: 35.0,
};

/**
 * Monthly prices per community subscription tier (in euros)
 */
export const communitySubscriptionPrices = {
  [SubscriptionType.FREE]: 0,
  [SubscriptionType.BASE]: 5.0,
  [SubscriptionType.PRO]: 10.0,
};

/**
 * Variable prices per member (in euros)
 */
export const businessVariableSubscriptionPrices = {
  [BusinessSubscriptionType.STARTER]: 0.1,
  [BusinessSubscriptionType.PROFESSIONAL]: 0.2,
};

const { STARTER, PROFESSIONAL } = BusinessSubscriptionType;

const bf = (i18nKey: string, tiers: BusinessSubscriptionType[], isComingSoon = false) => ({
  i18nKey,
  isComingSoon,
  [STARTER]:      tiers.includes(STARTER),
  [PROFESSIONAL]: tiers.includes(PROFESSIONAL),
});

/**
 * Feature list for business subscription cards.
 * Each entry declares which tiers include it.
 */
export const businessSubscriptionFeatures = [
  //Starter
  bf('sharingBetweenMembers', [STARTER, PROFESSIONAL]),
  bf('events', [STARTER, PROFESSIONAL]),
  bf('tasks', [STARTER, PROFESSIONAL]),
  bf('scores', [STARTER, PROFESSIONAL]),
  bf('costSplittingApp', [STARTER, PROFESSIONAL]),
  bf('memberAdministration', [STARTER, PROFESSIONAL]),
  bf('attendanceInsightAndExport', [STARTER, PROFESSIONAL]),
  bf('externalCalendars', [STARTER, PROFESSIONAL]),
  bf('basicFinancialAdministration', [STARTER, PROFESSIONAL]),
  bf('psd2BankConnection', [STARTER, PROFESSIONAL], true),
  bf('monthlyEmails1000', [STARTER, PROFESSIONAL], true),
  bf('maxPhotos5000', [STARTER, PROFESSIONAL]),
  bf('memberAdministration', [STARTER, PROFESSIONAL]),

  // Professional
  bf('financialAdministration', [PROFESSIONAL]),
  bf('crmConnection', [PROFESSIONAL]),
  bf('savingPots', [PROFESSIONAL], true),
  bf('budgetsPandLBalance', [PROFESSIONAL], true),
];

const starterFeatures: CommunityFeatures = {
  [Feature.EASY_SHARING]: true,
  [Feature.EVENTS]: true,
  [Feature.TASKS]: true,
  [Feature.STATS_AND_SCORES]: true,
  [Feature.COST_SPLITTING]: true,
  [Feature.CHATS]: true,
  [Feature.MEMBER_ADMINISTRATION]: true,
  [Feature.PHOTO_ALBUMS]: true,
  [Feature.LOCATIONS]: true,
  [Feature.FILES]: true,
  [Feature.FORMS]: true,
  [Feature.SALES]: true,
  [Feature.COMMUNICATION]: true,
  [Feature.PARTICIPATION_INSIGHTS]: true,
};

export const businessFeaturesInSubscription: Record<BusinessSubscriptionType, CommunityFeatures> = {
  [BusinessSubscriptionType.STARTER]: starterFeatures,
  [BusinessSubscriptionType.PROFESSIONAL]: {
    ...starterFeatures,
    [Feature.FINANCIAL_ADMINISTRATION]: true,
  },
};

export function getMinimumBusinessSubscriptionForFeatures(
  selectedFeatures: CommunityFeatures,
): BusinessSubscriptionType {
  const needsProfessional = Object.entries(selectedFeatures).some(
    ([key, value]) => value && key === Feature.FINANCIAL_ADMINISTRATION,
  );
  return needsProfessional ? BusinessSubscriptionType.PROFESSIONAL : BusinessSubscriptionType.STARTER;
}

export function isBusinessSubscriptionGreaterThanOrEqual(
  subscription: BusinessSubscriptionType,
  requiredSubscription: BusinessSubscriptionType,
): boolean {
  const subscriptionOrder: BusinessSubscriptionType[] = [
    BusinessSubscriptionType.STARTER,
    BusinessSubscriptionType.PROFESSIONAL,
  ];

  return subscriptionOrder.indexOf(subscription) >= subscriptionOrder.indexOf(requiredSubscription);
}

export function isBusinessSubscriptionGreaterThan(
  subscription: BusinessSubscriptionType,
  requiredSubscription: BusinessSubscriptionType,
): boolean {
  const subscriptionOrder: BusinessSubscriptionType[] = [
    BusinessSubscriptionType.STARTER,
    BusinessSubscriptionType.PROFESSIONAL,
  ];

  return subscriptionOrder.indexOf(subscription) > subscriptionOrder.indexOf(requiredSubscription);
}
